import React, { useState, useEffect } from 'react';
import {
  Link as RouterLink,
  useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  FormControl,
  Select,
  Pagination,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { formatDisplayDate } from '../utils/dateDisplay';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[12]
  }
}));

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://kundlistarstudio.com';
const BLOG_PAGE_SIZE = 18;
const BLOG_FEED_URL = `${API_BASE_URL}/blog/feed.json`;

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  description: string;
  createdAt: string;
  tags: string[];
  category: string;
  readingTime: number;
  author: string;
}

interface PaginationData {
  total: number;
  page: number;
  pages: number;
  limit: number;
}

const BlogList: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'astrology', label: 'Astrology' },
    { value: 'vedic', label: 'Vedic Astrology' },
    { value: 'kp', label: 'KP Astrology' },
    { value: 'nadi', label: 'Nadi Astrology' },
    { value: 'bnn', label: 'BNN Astrology' },
    { value: 'jaimini', label: 'Jaimini Astrology' },
    { value: 'western', label: 'Western Astrology' },
    { value: 'predictions', label: 'Predictions' },
    { value: 'numerology', label: 'Numerology' },
    { value: 'panchang', label: 'Panchang' },
    { value: 'cms', label: 'Practice Management' },
    { value: 'remedies', label: 'Remedies' },
    { value: 'general', label: 'General' }
  ];

  const page = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);
  const requestedCategory = searchParams.get('category') || 'all';
  const category = categories.some((cat) => cat.value === requestedCategory) ? requestedCategory : 'all';
  const appliedSearchTerm = (searchParams.get('q') || '').trim();

  const buildBlogUrl = (targetPage = page) => {
    const params = new URLSearchParams();
    if (targetPage > 1) params.set('page', String(targetPage));
    if (category !== 'all') params.set('category', category);
    if (appliedSearchTerm) params.set('q', appliedSearchTerm);

    const query = params.toString();
    return `${SITE_URL}/blog${query ? `?${query}` : ''}`;
  };

  const updateBlogUrl = ({
    nextPage = page,
    nextCategory = category,
    nextQuery = appliedSearchTerm
  }: {
    nextPage?: number;
    nextCategory?: string;
    nextQuery?: string;
  }) => {
    const params = new URLSearchParams();
    const normalizedPage = Math.max(nextPage, 1);
    const normalizedQuery = nextQuery.trim();

    if (normalizedPage > 1) params.set('page', String(normalizedPage));
    if (nextCategory !== 'all') params.set('category', nextCategory);
    if (normalizedQuery) params.set('q', normalizedQuery);

    setSearchParams(params);
  };

  useEffect(() => {
    fetchBlogPosts();
  }, [page, category, appliedSearchTerm]);

  useEffect(() => {
    setSearchTerm(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams({
        page: String(page),
        limit: String(BLOG_PAGE_SIZE),
        ...(category !== 'all' && { category }),
        ...(appliedSearchTerm && { q: appliedSearchTerm })
      });

      const response = await fetch(`${API_BASE_URL}/blog?${query}`);
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }

      const data = await response.json();
      setPosts(data.posts);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching blog posts');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    updateBlogUrl({ nextPage: 1, nextQuery: searchTerm });
  };

  const formatDate = (dateString: string) => {
    return formatDisplayDate(dateString);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    updateBlogUrl({ nextPage: value });
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Helmet>
        <title>Kundlistar Studio Blog | KP, Nadi, BNN &amp; Professional Astrology</title>
        <meta
          name="description"
          content="Read Kundlistar Studio articles about KP, Nadi, BNN, Vedic astrology, Numerology, chart analysis and professional astrology practice."
        />
        <meta
          name="keywords"
          content="Kundlistar Studio blog, KP astrology, Nadi astrology, BNN astrology, Vedic astrology, numerology, astrology practice management"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta property="og:title" content="Kundlistar Studio Blog" />
        <meta
          property="og:description"
          content="Explore KP, Nadi, BNN, Vedic astrology, Numerology and professional practice articles from Kundlistar Studio."
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Kundlistar Studio" />
        <meta property="og:url" content={buildBlogUrl()} />
        <link rel="canonical" href={buildBlogUrl()} />
        <link rel="alternate" type="application/feed+json" title="Kundlistar Studio Blog JSON Feed" href={BLOG_FEED_URL} />
        {page > 1 && <link rel="prev" href={buildBlogUrl(page - 1)} />}
        {pagination && page < pagination.pages && <link rel="next" href={buildBlogUrl(page + 1)} />}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'Kundlistar Studio Blog',
            description: 'KP, Nadi, BNN, Vedic astrology, Numerology and professional astrology practice articles.',
            url: `${SITE_URL}/blog`,
            publisher: {
              '@type': 'Organization',
              name: 'Kundlistar Studio',
              url: SITE_URL,
              logo: {
                '@type': 'ImageObject',
                url: `${SITE_URL}/logo512.png`
              }
            },
            blogPost: posts.map((post) => ({
              '@type': 'BlogPosting',
              headline: post.title,
              description: post.description,
              url: `${SITE_URL}/blog/${post.slug}`,
              datePublished: post.createdAt,
              author: {
                '@type': 'Organization',
                name: post.author || 'Kundlistar Studio Editorial Team'
              }
            }))
          })}
        </script>
      </Helmet>

      <Container component="main" maxWidth="lg" sx={{ py: 8 }}>
        {/* Hero Section */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Astrology Blog
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', fontSize: '1.1rem', mb: 4 }}>
            Explore insights into Nadi astrology, predictions, numerology, and cosmic wisdom
          </Typography>
        </Box>

        {/* Search and Filter Section */}
        <Box
          sx={{
            mb: 6,
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-end'
          }}
        >
          <TextField
            fullWidth
            label="Search articles"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by title or tags..."
            sx={{ flex: 1 }}
          />
          <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => {
                updateBlogUrl({ nextPage: 1, nextCategory: e.target.value });
              }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{
              minWidth: 120,
              height: 56,
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
          >
            Search
          </Button>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : posts.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            No blog posts found. Try adjusting your filters or search terms.
          </Alert>
        ) : (
          <>
            {/* Blog Posts Grid */}
            <Grid container spacing={4} sx={{ mb: 6 }}>
              {posts.map((post) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post._id}>
                  <Box
                    component={RouterLink}
                    to={`/blog/${post.slug}`}
                    aria-label={`Read ${post.title}`}
                    sx={{ display: 'block', height: '100%', color: 'inherit', textDecoration: 'none' }}
                  >
                    <StyledCard>
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Category Badge */}
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'inline-block',
                            backgroundColor: 'primary.main',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '4px',
                            textTransform: 'capitalize',
                            fontWeight: 600
                          }}
                        >
                          {post.category}
                        </Typography>
                      </Box>

                      {/* Title */}
                      <Typography
                        gutterBottom
                        variant="h6"
                        component="h2"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          minHeight: '3rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          mb: 1
                        }}
                      >
                        {post.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {post.description}
                      </Typography>

                      {/* Meta Info */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          fontSize: '0.85rem',
                          color: '#999',
                          mb: 2,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DateRangeIcon sx={{ fontSize: '1rem' }} />
                          <span>{formatDate(post.createdAt)}</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                          <span>{post.readingTime} min read</span>
                        </Box>
                      </Box>

                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                          {post.tags.slice(0, 2).map((tag) => (
                            <Typography
                              key={tag}
                              variant="caption"
                              sx={{
                                backgroundColor: '#f0f0f0',
                                px: 1,
                                py: 0.5,
                                borderRadius: '4px',
                                fontSize: '0.75rem'
                              }}
                            >
                              #{tag}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button
                        component="span"
                        variant="text"
                        sx={{ color: 'primary.main', fontWeight: 600 }}
                        fullWidth
                      >
                        Read More →
                      </Button>
                    </Box>
                  </StyledCard>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={pagination.pages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'primary.main'
                    },
                    '& .Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default BlogList;
